import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Wrench, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  RotateCcw, AlertCircle, Clock, Layers, CheckCircle2, Plus, Minus, Lock, Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modal de classificação de uma anomalia em quantidades:
 * - Defeito (perda total)
 * - Recuperada (recuperada na inspeção, não vai para lote)
 * - Arranjo (vai para lote ARR)
 *
 * Tabs:
 *   1. Quantidades — distribuição entre os 3 pools
 *   2. Tipos de Problema — atribuir tipo a peças arranjo e defeito
 */
export default function ClassificarQuantidadesModal({ open, onOpenChange, row, onConfirm }) {
  const total = row?.quantidade || 0;
  const minArranjo = row?.qtd_em_lote_arranjo || 0;

  const [arranjo, setArranjo] = useState(0);
  const [defeito, setDefeito] = useState(0);
  const [recuperada, setRecuperada] = useState(0);
  const [tipoArranjoId, setTipoArranjoId] = useState('');
  const [tipoDefeitoId, setTipoDefeitoId] = useState('');
  const [tiposIndividuaisArranjo, setTiposIndividuaisArranjo] = useState([]); // array por peça
  const [tiposIndividuaisDefeito, setTiposIndividuaisDefeito] = useState([]);
  const [modoIndividualArr, setModoIndividualArr] = useState(false);
  const [modoIndividualDef, setModoIndividualDef] = useState(false);
  const [tags, setTags] = useState([]); // tags para agrupamento ao criar lotes
  const [novaTag, setNovaTag] = useState('');
  const [tagsSugeridas, setTagsSugeridas] = useState([]);
  const [tiposProblema, setTiposProblema] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState('quantidades');

  useEffect(() => {
    if (row) {
      setArranjo(row.qtd_arranjo || 0);
      setDefeito(row.qtd_defeito || 0);
      setRecuperada(row.qtd_recuperada || 0);
      setTipoArranjoId(row.tipo_problema_arranjo_id || '');
      setTipoDefeitoId(row.tipo_problema_defeito_id || '');
      const tia = row.tipos_individuais_arranjo || [];
      const tid = row.tipos_individuais_defeito || [];
      setTiposIndividuaisArranjo(tia);
      setTiposIndividuaisDefeito(tid);
      // Restaurar modo individual se houver pelo menos um tipo individual definido
      setModoIndividualArr(tia.some(t => !!t));
      setModoIndividualDef(tid.some(t => !!t));
      setTags(row.tags || []);
      setNovaTag('');
      setTab('quantidades');
    }
  }, [row]);

  // Carregar tags: (1) sugeridas desta OF + (2) biblioteca global
  useEffect(() => {
    if (!open || !row?.of_id) return;
    Promise.all([
      axios.get(`${API}/anomalias-producao/grelha`, { params: { of_id: row.of_id } }).catch(() => ({ data: { rows: [] } })),
      axios.get(`${API}/anomalias-producao/tags/catalogo`).catch(() => ({ data: { tags: [] } })),
    ]).then(([grelhaRes, catRes]) => {
      const ofTags = new Set();
      (grelhaRes.data?.rows || []).forEach(r => (r.tags || []).forEach(t => ofTags.add(t)));
      const catalogo = (catRes.data?.tags || []).map(t => t.nome);
      // Juntar: primeiro OF, depois catalogo (sem duplicar)
      const todas = [...ofTags, ...catalogo.filter(n => !ofTags.has(n))];
      setTagsSugeridas(todas.slice(0, 30));
    });
  }, [open, row]);

  // Guardar no catálogo global quando user adiciona nova tag
  const persistirTagCatalogo = async (nome) => {
    try {
      await axios.post(`${API}/anomalias-producao/tags/catalogo`, { nome });
    } catch (_) { /* silencioso */ }
  };

  // Carregar tipos de anomalia (problemas) — apenas quando o modal abre
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/tipos-anomalia`)
      .then(res => setTiposProblema(res.data || []))
      .catch(() => setTiposProblema([]));
  }, [open]);

  const pendente = useMemo(() => Math.max(0, total - arranjo - defeito - recuperada), [total, arranjo, defeito, recuperada]);

  if (!row) return null;

  const setArranjoSafe = (n) => {
    const v = Math.max(minArranjo, Math.min(total - defeito - recuperada, n));
    setArranjo(v);
  };
  const setDefeitoSafe = (n) => {
    const v = Math.max(0, Math.min(total - arranjo - recuperada, n));
    setDefeito(v);
  };
  const setRecuperadaSafe = (n) => {
    const v = Math.max(0, Math.min(total - arranjo - defeito, n));
    setRecuperada(v);
  };

  // Mover do pool pendente para um destino
  const moverPendentePara = (destino, n) => {
    const m = Math.min(pendente, n);
    if (destino === 'arranjo') setArranjo(arranjo + m);
    if (destino === 'defeito') setDefeito(defeito + m);
    if (destino === 'recuperada') setRecuperada(recuperada + m);
  };
  // Mover entre pools (arranjo respeita minArranjo)
  const moverEntre = (de, para, n) => {
    let movel = 0;
    if (de === 'arranjo') movel = Math.max(0, arranjo - minArranjo);
    if (de === 'defeito') movel = defeito;
    if (de === 'recuperada') movel = recuperada;
    const m = Math.min(movel, n);
    if (de === 'arranjo') setArranjo(arranjo - m);
    if (de === 'defeito') setDefeito(defeito - m);
    if (de === 'recuperada') setRecuperada(recuperada - m);
    if (para === 'arranjo') setArranjo(prev => prev + m);
    if (para === 'defeito') setDefeito(prev => prev + m);
    if (para === 'recuperada') setRecuperada(prev => prev + m);
  };

  const tudoArranjo = () => { setArranjo(total - defeito - recuperada); };
  const tudoDefeito = () => { setDefeito(total - arranjo - recuperada); };
  const tudoRecuperada = () => { setRecuperada(total - arranjo - defeito); };
  const reset = () => { setArranjo(minArranjo); setDefeito(0); setRecuperada(0); };
  const desclassificar = () => { setArranjo(minArranjo); setDefeito(0); setRecuperada(0); };

  const handleConfirm = async () => {
    // Se há peças pendentes, avisar o utilizador antes de guardar
    if (pendente > 0) {
      const ok = window.confirm(
        `Atenção: ainda há ${pendente} peça(s) sem classificação definida.\n\n` +
        `Peça(s) não classificada(s) permanecerão como "pendente" e aparecerão destacadas na grelha.\n\n` +
        `Deseja guardar assim mesmo?`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      // Ajustar arrays individuais ao tamanho actual
      const arrIndividual = modoIndividualArr
        ? Array.from({ length: arranjo }, (_, i) => tiposIndividuaisArranjo[i] || '')
        : [];
      const defIndividual = modoIndividualDef
        ? Array.from({ length: defeito }, (_, i) => tiposIndividuaisDefeito[i] || '')
        : [];
      
      await onConfirm({
        qtd_arranjo: arranjo,
        qtd_defeito: defeito,
        qtd_recuperada: recuperada,
        tipo_problema_arranjo_id: modoIndividualArr ? null : (tipoArranjoId || null),
        tipo_problema_defeito_id: modoIndividualDef ? null : (tipoDefeitoId || null),
        tipos_individuais_arranjo: arrIndividual,
        tipos_individuais_defeito: defIndividual,
        tags: tags,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const rast = row.rastreabilidade || {};
  const fasePct = rast.ops_total ? Math.round((rast.ops_completadas / rast.ops_total) * 100) : 0;

  const StepBtn = ({ onClick, disabled, children, testId, label }) => (
    <Button size="icon" variant="outline" onClick={onClick} disabled={disabled} title={label} data-testid={testId} className="h-9 w-9">
      {children}
    </Button>
  );

  // Coluna de pool reutilizável
  const PoolCol = ({ tipo, count, color, bgClass, borderClass, textClass, icon, descricao, ondeVai, max, setSafe, tudoFn, dataTestId, compact = false }) => (
    <div className={`${compact ? 'p-3' : 'p-4'} rounded-lg ${bgClass} border-2 ${borderClass}`} data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-1">
        <div className={`flex items-center gap-2 ${textClass}`}>
          {icon}
          <h3 className={`font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>{tipo}</h3>
        </div>
        <Button size="sm" variant="outline" className={`h-7 text-[11px] px-2.5 border-2 ${borderClass} ${textClass} hover:opacity-90`} onClick={tudoFn} data-testid={`tudo-${color}-btn`}>
          Tudo
        </Button>
      </div>
      <p className={`text-[11px] ${textClass} opacity-70 mb-1`}>{descricao}</p>
      <p className={`text-[10px] ${textClass} opacity-60 mb-2`}>→ {ondeVai}</p>
      <div className="text-center">
        <div className="flex items-center justify-center gap-1 mb-2">
          {!compact && (
            <Button size="icon" variant="outline" onClick={() => setSafe(count - 5)} disabled={count <= (color === 'amber' ? minArranjo : 0)} className="h-10 w-10" title="-5" data-testid={`${color}-minus5`}>
              <span className="text-[10px] font-bold">-5</span>
            </Button>
          )}
          <Button size="icon" variant="outline" onClick={() => setSafe(count - 1)} disabled={count <= (color === 'amber' ? minArranjo : 0)} className={compact ? 'h-9 w-9' : 'h-12 w-12'} data-testid={`${color}-minus`}>
            <Minus className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
          </Button>
          <p className={`${compact ? 'text-3xl' : 'text-5xl'} font-bold ${textClass} mx-2 min-w-[50px]`} data-testid={`${color}-count`}>{count}</p>
          <Button size="icon" variant="outline" onClick={() => setSafe(count + 1)} disabled={count >= max} className={compact ? 'h-9 w-9' : 'h-12 w-12'} data-testid={`${color}-plus`}>
            <Plus className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
          </Button>
          {!compact && (
            <Button size="icon" variant="outline" onClick={() => setSafe(count + 5)} disabled={count >= max} className="h-10 w-10" title="+5" data-testid={`${color}-plus5`}>
              <span className="text-[10px] font-bold">+5</span>
            </Button>
          )}
        </div>
        {color === 'amber' && minArranjo > 0 && (
          <p className="text-[10px] text-rose-700 mt-1 flex items-center justify-center gap-1" title="Peças já alocadas a um lote ARR — não é possível reduzir abaixo deste valor">
            <Lock className="h-3 w-3" /> Mínimo: {minArranjo} (em lote ARR)
          </p>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto" data-testid="classificar-quantidades-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-amber-600" />
            Classificar Anomalia
          </DialogTitle>
          <DialogDescription>
            Divida as <strong>{total}</strong> peças entre <strong className="text-red-700">Defeito</strong>, <strong className="text-green-700">Recuperada</strong> e <strong className="text-amber-700">Arranjo</strong>. O resto fica pendente.
          </DialogDescription>
        </DialogHeader>

        {/* Contexto + Rastreabilidade (sempre visível) */}
        <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div><p className="text-[10px] text-stone-500 uppercase">OF</p><p className="font-semibold text-stone-800">{row.of_numero || '—'}</p></div>
            <div><p className="text-[10px] text-stone-500 uppercase">Lote</p><p className="font-semibold text-stone-800">{row.lote_codigo || '—'}</p></div>
            <div><p className="text-[10px] text-stone-500 uppercase">Operação</p><p className="font-semibold text-stone-800">{row.operacao_codigo} {row.operacao_nome}</p></div>
            <div><p className="text-[10px] text-stone-500 uppercase">Operador</p><p className="font-semibold text-stone-800">{row.operador_nome || '—'}</p></div>
          </div>
          <div className="border-t border-stone-200 pt-2 grid grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-blue-600" /><span className="text-stone-500">Fase:</span><span className="font-semibold text-stone-800">{rast.ops_completadas || 0}/{rast.ops_total || 0} ({fasePct}%)</span></div>
            <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-amber-600" /><span className="text-stone-500">T.Restante:</span><span className="font-semibold text-stone-800">{rast.tempo_restante_min || 0} min</span></div>
            <div className="flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5 text-stone-500" /><span className="text-stone-500">Em falta:</span><span className="font-semibold text-stone-800">{rast.ops_em_falta || 0} ops</span></div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 h-12 bg-stone-100 p-1.5 gap-1" data-testid="classificar-tabs">
            <TabsTrigger
              value="quantidades"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-white data-[state=active]:shadow-md text-sm font-semibold"
              data-testid="tab-quantidades"
            >
              1. Quantidades
            </TabsTrigger>
            <TabsTrigger
              value="tipos"
              className="data-[state=active]:bg-amber-600 data-[state=active]:text-white data-[state=active]:shadow-md text-sm font-semibold"
              data-testid="tab-tipos"
            >
              2. Tipos &amp; Tags
            </TabsTrigger>
          </TabsList>

          {/* TAB QUANTIDADES */}
          <TabsContent value="quantidades" className="space-y-3 mt-3">
            {/* Banner — peças bloqueadas porque já estão alocadas a um lote ARR */}
            {minArranjo > 0 && (
              <div className="rounded-lg bg-rose-50 border-2 border-rose-300 p-3 flex items-start gap-2.5" data-testid="banner-pecas-bloqueadas">
                <Package className="h-5 w-5 text-rose-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-rose-800">
                    {minArranjo} {minArranjo === 1 ? 'peça já alocada' : 'peças já alocadas'} a um Lote de Arranjo
                  </p>
                  <p className="text-[12px] text-rose-700 mt-0.5">
                    {(row?.lotes_arranjo || []).length > 0 ? (
                      <>Lote(s): <span className="font-mono font-bold">{(row.lotes_arranjo || []).map(l => l.codigo).join(', ')}</span> — </>
                    ) : null}
                    Não é possível reduzir o nº de Arranjo abaixo de <strong>{minArranjo}</strong>. Para libertar estas peças, elimine primeiro o(s) lote(s) ARR.
                  </p>
                </div>
              </div>
            )}
            {/* Banner de classificação atual (se houver) */}
            {(arranjo > 0 || defeito > 0 || recuperada > 0) && pendente === 0 && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5 flex items-center justify-between" data-testid="banner-ja-classificada">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                  <span className="text-emerald-800 font-medium">Já classificada:</span>
                  {defeito > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold text-xs">{defeito} Defeito</span>}
                  {arranjo > 0 && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-bold text-xs">{arranjo} Arranjo</span>}
                  {recuperada > 0 && <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 font-bold text-xs">{recuperada} Recuperada</span>}
                </div>
                <Button size="sm" variant="ghost" className="text-xs h-7 text-emerald-700" onClick={() => setTab('tipos')} data-testid="ir-tipos-btn">
                  Editar Tipos →
                </Button>
              </div>
            )}
            {(arranjo > 0 || defeito > 0 || recuperada > 0) && pendente > 0 && (
              <div className="rounded-lg bg-orange-50 border border-orange-200 p-2.5 flex items-center gap-2 text-sm" data-testid="banner-parcial">
                <Clock className="h-4 w-4 text-orange-700" />
                <span className="text-orange-800 font-medium">Parcial — {pendente} ainda pendente(s)</span>
                {defeito > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold text-xs">{defeito}D</span>}
                {arranjo > 0 && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-bold text-xs">{arranjo}A</span>}
                {recuperada > 0 && <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 font-bold text-xs">{recuperada}R</span>}
              </div>
            )}

            {/* Resumo */}
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded bg-stone-100 border border-stone-200 text-center">
                <p className="text-[10px] text-stone-500 uppercase">Total</p>
                <p className="text-2xl font-bold text-stone-800">{total}</p>
              </div>
              <div className={`p-2 rounded border-2 text-center transition ${pendente > 0 ? 'bg-yellow-50 border-yellow-500 ring-2 ring-yellow-300 animate-pulse-slow' : 'bg-emerald-50 border-emerald-300'}`}>
                <p className={`text-[10px] uppercase font-semibold ${pendente > 0 ? 'text-yellow-800' : 'text-emerald-700'}`}>Pendentes</p>
                <p className={`text-2xl font-bold ${pendente > 0 ? 'text-yellow-700' : 'text-emerald-700'}`} data-testid="pendente-count">{pendente}</p>
                {pendente > 0 && <p className="text-[9px] text-yellow-700 font-bold uppercase">⚠ classifique-as</p>}
              </div>
              <div className="p-2 rounded bg-stone-50 border border-stone-200 text-center">
                <p className="text-[10px] text-stone-500 uppercase">Atribuídas</p>
                <p className="text-2xl font-bold text-stone-800">{arranjo + defeito + recuperada}</p>
              </div>
            </div>

            {/* 3 colunas: Defeito | Arranjo | Recuperada (mais pequena à direita) */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_0.85fr] gap-3">
              <PoolCol
                tipo="Defeito"
                count={defeito}
                color="red"
                bgClass="bg-red-50"
                borderClass="border-red-200"
                textClass="text-red-700"
                icon={<X className="h-5 w-5" />}
                descricao="Peça inutilizável — perda total"
                ondeVai="descartada"
                max={total - arranjo - recuperada}
                setSafe={setDefeitoSafe}
                tudoFn={tudoDefeito}
                dataTestId="coluna-defeito"
              />
              <PoolCol
                tipo="Arranjo"
                count={arranjo}
                color="amber"
                bgClass="bg-amber-50"
                borderClass="border-amber-300"
                textClass="text-amber-700"
                icon={<Wrench className="h-5 w-5" />}
                descricao="Peça recuperável — vai para lote"
                ondeVai="lote ARR"
                max={total - defeito - recuperada}
                setSafe={setArranjoSafe}
                tudoFn={tudoArranjo}
                dataTestId="coluna-arranjo"
              />
              <PoolCol
                tipo="Recuperada"
                count={recuperada}
                color="green"
                bgClass="bg-green-50"
                borderClass="border-green-200"
                textClass="text-green-700"
                icon={<CheckCircle2 className="h-4 w-4" />}
                descricao="Já recuperada na inspeção"
                ondeVai="produção"
                max={total - arranjo - defeito}
                setSafe={setRecuperadaSafe}
                tudoFn={tudoRecuperada}
                dataTestId="coluna-recuperada"
                compact
              />
            </div>

            {/* Mover do Pendente */}
            {pendente > 0 && (
              <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-yellow-50 border border-yellow-200">
                <span className="text-xs text-yellow-700 font-medium">Mover {pendente}:</span>
                <Button size="sm" variant="outline" onClick={() => moverPendentePara('defeito', pendente)} className="border-red-300 text-red-700 h-7 text-xs" data-testid="pendente-to-defeito">→ Defeito</Button>
                <Button size="sm" variant="outline" onClick={() => moverPendentePara('recuperada', pendente)} className="border-green-300 text-green-700 h-7 text-xs" data-testid="pendente-to-recuperada">→ Recuperada</Button>
                <Button size="sm" variant="outline" onClick={() => moverPendentePara('arranjo', pendente)} className="border-amber-300 text-amber-700 h-7 text-xs" data-testid="pendente-to-arranjo">→ Arranjo</Button>
              </div>
            )}
          </TabsContent>

          {/* TAB TIPOS DE PROBLEMA */}
          <TabsContent value="tipos" className="space-y-3 mt-3">
            <p className="text-xs text-stone-600">
              Atribua o tipo de problema. Pode aplicar um tipo a <strong>todas as peças</strong> de um pool ou definir <strong>individualmente</strong> peça a peça.
            </p>

            {/* Banner de aviso: peças sem tipo no modo individual */}
            {(() => {
              const semTipoArr = modoIndividualArr ? Array.from({ length: arranjo }).filter((_, i) => !tiposIndividuaisArranjo[i]).length : 0;
              const semTipoDef = modoIndividualDef ? Array.from({ length: defeito }).filter((_, i) => !tiposIndividuaisDefeito[i]).length : 0;
              const total = semTipoArr + semTipoDef;
              if (total === 0) return null;
              return (
                <div className="p-2.5 rounded-lg bg-yellow-100 border-2 border-yellow-400 flex items-center gap-2" data-testid="aviso-pecas-sem-tipo">
                  <AlertCircle className="h-4 w-4 text-yellow-700 flex-shrink-0" />
                  <p className="text-xs text-yellow-900">
                    <strong>{total} {total === 1 ? 'peça sem tipo definido' : 'peças sem tipo definido'}</strong>
                    {semTipoArr > 0 && <span className="ml-1">· Arranjo: {semTipoArr}</span>}
                    {semTipoDef > 0 && <span className="ml-1">· Defeito: {semTipoDef}</span>}
                  </p>
                </div>
              );
            })()}
            
            {tiposProblema.length === 0 && (
              <div className="text-center py-4 text-stone-400 text-xs">
                Nenhum tipo de problema configurado. Crie tipos na aba "Tipos de Anomalias".
              </div>
            )}

            {tiposProblema.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Bloco Arranjo */}
                <div className={`p-3 rounded-lg border ${arranjo > 0 ? 'bg-amber-50 border-amber-300' : 'bg-stone-50 border-stone-200 opacity-60'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <Wrench className="h-3.5 w-3.5 text-amber-700" />
                      <h3 className="font-semibold text-amber-700 text-xs">Peças Arranjo ({arranjo})</h3>
                    </div>
                    {arranjo > 0 && (
                      <button
                        type="button"
                        onClick={() => setModoIndividualArr(v => !v)}
                        className="text-[10px] text-amber-700 hover:text-amber-900 underline"
                        data-testid="toggle-modo-individual-arranjo"
                      >
                        {modoIndividualArr ? '← Aplicar a todas' : 'Definir individualmente →'}
                      </button>
                    )}
                  </div>
                  
                  {!modoIndividualArr && (
                    <select
                      value={tipoArranjoId}
                      onChange={e => setTipoArranjoId(e.target.value)}
                      disabled={arranjo === 0}
                      className="w-full px-2 py-1 border border-amber-200 rounded text-xs bg-white h-8"
                      data-testid="tipo-arranjo-select"
                    >
                      <option value="">— sem tipo —</option>
                      {tiposProblema
                        .filter(t => !t.classificacao || t.classificacao === 'arranjo' || t.classificacao === 'ambos')
                        .map(t => (<option key={t.id} value={t.id}>{t.nome || t.codigo}</option>))}
                    </select>
                  )}
                  
                  {modoIndividualArr && arranjo > 0 && (
                    <div className="space-y-1 max-h-[280px] overflow-y-auto" data-testid="tipos-individuais-arranjo">
                      {Array.from({ length: arranjo }).map((_, idx) => {
                        const semTipo = !tiposIndividuaisArranjo[idx];
                        return (
                        <div key={idx} className={`flex items-center gap-1.5 ${semTipo ? 'bg-yellow-100/70 border border-yellow-300 rounded px-1.5 py-0.5' : ''}`}>
                          <span className={`text-[10px] font-mono w-6 flex-shrink-0 ${semTipo ? 'text-yellow-800 font-bold' : 'text-amber-700'}`}>#{idx + 1}</span>
                          {semTipo && <AlertCircle className="h-3 w-3 text-yellow-700 flex-shrink-0" />}
                          <select
                            value={tiposIndividuaisArranjo[idx] || ''}
                            onChange={e => {
                              const novo = [...tiposIndividuaisArranjo];
                              novo[idx] = e.target.value;
                              setTiposIndividuaisArranjo(novo);
                            }}
                            className={`flex-1 px-1.5 py-0.5 border rounded text-[11px] bg-white h-7 ${semTipo ? 'border-yellow-400 ring-1 ring-yellow-300' : 'border-amber-200'}`}
                            data-testid={`tipo-arranjo-individual-${idx}`}
                          >
                            <option value="">— sem tipo —</option>
                            {tiposProblema
                              .filter(t => !t.classificacao || t.classificacao === 'arranjo' || t.classificacao === 'ambos')
                              .map(t => (<option key={t.id} value={t.id}>{t.nome || t.codigo}</option>))}
                          </select>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Bloco Defeito */}
                <div className={`p-3 rounded-lg border ${defeito > 0 ? 'bg-red-50 border-red-300' : 'bg-stone-50 border-stone-200 opacity-60'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <X className="h-3.5 w-3.5 text-red-700" />
                      <h3 className="font-semibold text-red-700 text-xs">Peças Defeito ({defeito})</h3>
                    </div>
                    {defeito > 0 && (
                      <button
                        type="button"
                        onClick={() => setModoIndividualDef(v => !v)}
                        className="text-[10px] text-red-700 hover:text-red-900 underline"
                        data-testid="toggle-modo-individual-defeito"
                      >
                        {modoIndividualDef ? '← Aplicar a todas' : 'Definir individualmente →'}
                      </button>
                    )}
                  </div>
                  
                  {!modoIndividualDef && (
                    <select
                      value={tipoDefeitoId}
                      onChange={e => setTipoDefeitoId(e.target.value)}
                      disabled={defeito === 0}
                      className="w-full px-2 py-1 border border-red-200 rounded text-xs bg-white h-8"
                      data-testid="tipo-defeito-select"
                    >
                      <option value="">— sem tipo —</option>
                      {tiposProblema
                        .filter(t => !t.classificacao || t.classificacao === 'defeito' || t.classificacao === 'ambos')
                        .map(t => (<option key={t.id} value={t.id}>{t.nome || t.codigo}</option>))}
                    </select>
                  )}
                  
                  {modoIndividualDef && defeito > 0 && (
                    <div className="space-y-1 max-h-[280px] overflow-y-auto" data-testid="tipos-individuais-defeito">
                      {Array.from({ length: defeito }).map((_, idx) => {
                        const semTipo = !tiposIndividuaisDefeito[idx];
                        return (
                        <div key={idx} className={`flex items-center gap-1.5 ${semTipo ? 'bg-yellow-100/70 border border-yellow-300 rounded px-1.5 py-0.5' : ''}`}>
                          <span className={`text-[10px] font-mono w-6 flex-shrink-0 ${semTipo ? 'text-yellow-800 font-bold' : 'text-red-700'}`}>#{idx + 1}</span>
                          {semTipo && <AlertCircle className="h-3 w-3 text-yellow-700 flex-shrink-0" />}
                          <select
                            value={tiposIndividuaisDefeito[idx] || ''}
                            onChange={e => {
                              const novo = [...tiposIndividuaisDefeito];
                              novo[idx] = e.target.value;
                              setTiposIndividuaisDefeito(novo);
                            }}
                            className={`flex-1 px-1.5 py-0.5 border rounded text-[11px] bg-white h-7 ${semTipo ? 'border-yellow-400 ring-1 ring-yellow-300' : 'border-red-200'}`}
                            data-testid={`tipo-defeito-individual-${idx}`}
                          >
                            <option value="">— sem tipo —</option>
                            {tiposProblema
                              .filter(t => !t.classificacao || t.classificacao === 'defeito' || t.classificacao === 'ambos')
                              .map(t => (<option key={t.id} value={t.id}>{t.nome || t.codigo}</option>))}
                          </select>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags (agrupamento - só arranjo) */}
            <div className="p-3 rounded-lg border border-violet-200 bg-violet-50/40" data-testid="tags-section">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-violet-600 text-white text-xs font-bold">#</span>
                <div className="flex-1">
                  <h3 className="font-semibold text-violet-800 text-sm flex items-center gap-1.5">
                    Tags de Agrupamento
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase border border-amber-200">só Arranjo</span>
                  </h3>
                  <p className="text-[11px] text-violet-700/70">As tags aplicam-se apenas a peças de <strong>Arranjo</strong> para ajudar a agrupá-las ao criar lotes ARR. Peças <strong>Defeito</strong> são perdas totais e não usam tags.</p>
                </div>
              </div>
              {arranjo === 0 && (
                <div className="mb-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  Esta anomalia não tem peças de Arranjo — as tags não se aplicam.
                </div>
              )}
              {/* Tags actuais (aplicadas) */}
              {tags.length > 0 && (
                <div className="mb-3" data-testid="tags-list">
                  <p className="text-[11px] uppercase font-bold text-violet-700 mb-1.5 tracking-wide">Tags aplicadas ({tags.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <span key={`applied-${t}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-600 text-white text-sm font-semibold shadow-sm" data-testid={`tag-${t}`}>
                        # {t}
                        <button
                          type="button"
                          onClick={() => setTags(tags.filter(x => x !== t))}
                          className="hover:bg-white/20 rounded-full p-0.5 transition"
                          data-testid={`tag-remove-${t}`}
                          aria-label={`Remover tag ${t}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Input nova tag */}
              <div className="flex items-stretch gap-2">
                <Input
                  value={novaTag}
                  onChange={(e) => setNovaTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const t = (novaTag || '').trim();
                      if (t && !tags.some(x => x.toLowerCase() === t.toLowerCase())) {
                        setTags([...tags, t.slice(0, 32)]);
                        persistirTagCatalogo(t.slice(0, 32));
                      }
                      setNovaTag('');
                    }
                  }}
                  placeholder="Escreva uma tag e prima Enter…"
                  className="h-11 text-sm border-violet-300 focus-visible:ring-violet-400"
                  data-testid="tag-input"
                />
                <Button
                  onClick={() => {
                    const t = (novaTag || '').trim();
                    if (t && !tags.some(x => x.toLowerCase() === t.toLowerCase())) {
                      setTags([...tags, t.slice(0, 32)]);
                      persistirTagCatalogo(t.slice(0, 32));
                    }
                    setNovaTag('');
                  }}
                  disabled={!novaTag.trim()}
                  className="h-11 px-4 text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white disabled:bg-stone-200 disabled:text-stone-400"
                  data-testid="tag-add-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Adicionar
                </Button>
              </div>
              {/* Biblioteca / Sugestões */}
              {tagsSugeridas.length > 0 && (
                <div className="mt-4 pt-3 border-t border-violet-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[12px] uppercase font-bold text-violet-700 tracking-wide">Biblioteca de tags</p>
                    <span className="text-[11px] text-violet-600/70 italic">Clique para adicionar · × para eliminar</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tagsSugeridas
                      .filter(t => !tags.some(x => x.toLowerCase() === t.toLowerCase()))
                      .slice(0, 24)
                      .map((t) => (
                        <span
                          key={`sug-${t}`}
                          className="group inline-flex items-stretch rounded-full bg-white border-2 border-violet-200 text-violet-700 hover:border-violet-400 hover:shadow-sm transition overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={() => setTags([...tags, t])}
                            className="px-3 py-1.5 text-sm font-medium hover:bg-violet-50"
                            data-testid={`tag-sugestao-${t}`}
                          >
                            <span className="text-violet-500 font-bold mr-0.5">+</span> {t}
                          </button>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Eliminar a tag "${t}" da biblioteca? (não afeta anomalias existentes)`)) return;
                              try {
                                await axios.delete(`${API}/anomalias-producao/tags/catalogo/${encodeURIComponent(t)}`);
                                setTagsSugeridas(prev => prev.filter(x => x.toLowerCase() !== t.toLowerCase()));
                              } catch (err) {
                                alert('Erro ao eliminar tag: ' + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="px-2 border-l border-violet-200 text-violet-400 hover:text-white hover:bg-red-500 transition"
                            title="Eliminar da biblioteca"
                            data-testid={`tag-sugestao-delete-${t}`}
                            aria-label={`Eliminar tag ${t} da biblioteca`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-stone-200 flex-wrap gap-2">
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={reset} className="text-stone-500" data-testid="reset-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
            {(arranjo > minArranjo || defeito > 0 || recuperada > 0) && (
              <Button variant="ghost" size="sm" onClick={desclassificar} className="text-yellow-700 hover:text-yellow-800 hover:bg-yellow-50" data-testid="desclassificar-btn">
                ↺ Desclassificar tudo
              </Button>
            )}
            {minArranjo > 0 && (
              <span className="text-xs text-blue-600 self-center" title="Já em lote — para libertar elimine primeiro o lote ARR">
                {minArranjo} em lote
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            {tab === 'quantidades' ? (
              <Button
                onClick={() => setTab('tipos')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="avancar-tipos-btn"
                disabled={arranjo === 0 && defeito === 0}
              >
                Avançar → Tipos &amp; Tags
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setTab('quantidades')} className="border-blue-300 text-blue-700" data-testid="voltar-quantidades-btn">
                ← Voltar
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              className={pendente > 0 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="confirmar-classificacao-btn"
            >
              {submitting ? 'A guardar...' : pendente > 0 ? `Guardar (${pendente} pend.)` : 'Confirmar Classificação'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
